import type { Profile } from '@/types/profile'
import type { Switch } from '@/types/switch'

function iso(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

function pick<T>(raw: Record<string, unknown>, camel: string, snake: string): T {
  return (raw[camel] ?? raw[snake]) as T
}

/** Map API switch (camelCase from Drizzle JSON) → frontend Switch (snake_case). */
export function mapSwitch(
  raw: Record<string, unknown>,
  opts?: { userId?: string; forceOwner?: boolean },
): Switch {
  const ownerId = String(pick(raw, 'ownerId', 'owner_id') ?? '')
  const recipient1 = (pick(raw, 'recipient1Id', 'recipient1_id') as string | null) ?? null
  const recipient2 = (pick(raw, 'recipient2Id', 'recipient2_id') as string | null) ?? null
  const recipient3 = (pick(raw, 'recipient3Id', 'recipient3_id') as string | null) ?? null
  const recipient4 = (pick(raw, 'recipient4Id', 'recipient4_id') as string | null) ?? null
  const recipient5 = (pick(raw, 'recipient5Id', 'recipient5_id') as string | null) ?? null
  const state = (pick(raw, 'state', 'state') as Switch['state']) || 'safe'
  const switchType =
    (pick(raw, 'switchType', 'switch_type') as Switch['switch_type']) || 'check_in'
  const recipientEmailsRaw = pick(raw, 'recipientEmails', 'recipient_emails')
  const recipientEmails = Array.isArray(recipientEmailsRaw)
    ? recipientEmailsRaw.filter((value): value is string => typeof value === 'string')
    : []
  const contents =
    (pick(raw, 'contents', 'contents') as string | null) ??
    (pick(raw, 'encryptedContents', 'encrypted_contents') as string | null) ??
    null

  const isOwner =
    opts?.forceOwner === true || (opts?.userId != null && opts.userId === ownerId)
  const recipientIds = [recipient1, recipient2, recipient3, recipient4, recipient5].filter(Boolean)
  const isRecipient =
    opts?.forceOwner === false ||
    (opts?.userId != null && !isOwner && recipientIds.includes(opts.userId))

  return {
    id: String(raw.id),
    owner_id: ownerId,
    title: String(raw.title ?? ''),
    contents,
    state,
    switch_type: switchType === 'scheduled' ? 'scheduled' : 'check_in',
    last_check_in: iso(pick(raw, 'lastCheckIn', 'last_check_in')),
    check_in_frequency_days:
      (pick(raw, 'checkInFrequencyDays', 'check_in_frequency_days') as number | null) ?? null,
    trigger_offset_days:
      (pick(raw, 'triggerOffsetDays', 'trigger_offset_days') as number | null) ?? null,
    arm_date: iso(pick(raw, 'armDate', 'arm_date')),
    trigger_date: iso(pick(raw, 'triggerDate', 'trigger_date')),
    created_at: iso(pick(raw, 'createdAt', 'created_at')) ?? new Date().toISOString(),
    updated_at: iso(pick(raw, 'updatedAt', 'updated_at')) ?? new Date().toISOString(),
    recipient1_id: recipient1,
    recipient2_id: recipient2,
    recipient3_id: recipient3,
    recipient4_id: recipient4,
    recipient5_id: recipient5,
    recipient_emails: recipientEmails,
    isOwner,
    isRecipient: Boolean(isRecipient),
    canAccessContent: isOwner || (Boolean(isRecipient) && state === 'triggered'),
    hasContent: Boolean(contents),
    last_first_reminder_at: iso(pick(raw, 'lastFirstReminderAt', 'last_first_reminder_at')),
    last_second_reminder_at: iso(pick(raw, 'lastSecondReminderAt', 'last_second_reminder_at')),
    last_armed_notification_at: iso(
      pick(raw, 'lastArmedNotificationAt', 'last_armed_notification_at'),
    ),
    last_triggered_notification_at: iso(
      pick(raw, 'lastTriggeredNotificationAt', 'last_triggered_notification_at'),
    ),
  }
}

/** Map API profile → frontend Profile (snake_case). */
export function mapProfile(
  raw: Record<string, unknown> | null | undefined,
  emailFallback?: string | null,
): Profile | null {
  if (!raw) return null
  return {
    id: String(raw.id),
    full_name: (pick(raw, 'fullName', 'full_name') as string | null) ?? null,
    email: (pick(raw, 'email', 'email') as string | null) ?? emailFallback ?? null,
    pin_hash: (pick(raw, 'pinHash', 'pin_hash') as string | null) ?? null,
    last_pin_entry: iso(pick(raw, 'lastPinEntry', 'last_pin_entry')),
    failed_pin_attempts: (pick(raw, 'failedPinAttempts', 'failed_pin_attempts') as number | null) ?? 0,
    locked_until: iso(pick(raw, 'lockedUntil', 'locked_until')),
    subscription_tier:
      (pick(raw, 'subscriptionTier', 'subscription_tier') as Profile['subscription_tier']) || 'free',
    subscription_start_date: iso(pick(raw, 'subscriptionStartDate', 'subscription_start_date')),
    subscription_end_date: iso(pick(raw, 'subscriptionEndDate', 'subscription_end_date')),
    storage_used: Number(pick(raw, 'storageUsed', 'storage_used') ?? 0),
    storage_limit: Number(pick(raw, 'storageLimit', 'storage_limit') ?? 104857600),
    profile_type: (pick(raw, 'profileType', 'profile_type') as Profile['profile_type']) ?? 'normal',
    pin_required: Boolean(pick(raw, 'pinRequired', 'pin_required')),
    stripe_subscription_id:
      (pick(raw, 'stripeSubscriptionId', 'stripe_subscription_id') as string | null) ?? null,
    stripe_subscription_status:
      (pick(raw, 'stripeSubscriptionStatus', 'stripe_subscription_status') as Profile['stripe_subscription_status']) ??
      null,
    stripe_subscription_price_id:
      (pick(raw, 'stripeSubscriptionPriceId', 'stripe_subscription_price_id') as string | null) ??
      null,
    stripe_subscription_period_start: iso(
      pick(raw, 'stripeSubscriptionPeriodStart', 'stripe_subscription_period_start'),
    ),
    stripe_subscription_period_end: iso(
      pick(raw, 'stripeSubscriptionPeriodEnd', 'stripe_subscription_period_end'),
    ),
    stripe_customer_id:
      (pick(raw, 'stripeCustomerId', 'stripe_customer_id') as string | null) ?? null,
    stripe_subscription_cancel_at_period_end: Boolean(
      pick(raw, 'stripeSubscriptionCancelAtPeriodEnd', 'stripe_subscription_cancel_at_period_end'),
    ),
  }
}

export function mapSwitchFile(raw: Record<string, unknown>) {
  return {
    id: String(raw.id),
    switch_id: String(pick(raw, 'switchId', 'switch_id') ?? ''),
    owner_id: (pick(raw, 'ownerId', 'owner_id') as string | null) ?? null,
    filename: String(raw.filename ?? ''),
    original_name: String(pick(raw, 'originalName', 'original_name') ?? ''),
    original_type: String(pick(raw, 'originalType', 'original_type') ?? ''),
    content_type: String(pick(raw, 'contentType', 'content_type') ?? ''),
    mime_type: (pick(raw, 'mimeType', 'mime_type') as string | null) ?? null,
    size: Number(raw.size ?? 0),
    storage_path: String(pick(raw, 'storagePath', 'storage_path') ?? ''),
    created_at: iso(pick(raw, 'createdAt', 'created_at')),
    updated_at: iso(pick(raw, 'updatedAt', 'updated_at')),
  }
}
